import React, { useState, useEffect } from 'react';
import { TrendUp, Medal } from '@phosphor-icons/react';
import { api } from '../data/api';

export default function Progress({ user, workoutLogs, today, showToast }) {
  const [subTab, setSubTab] = useState('measurements');
  const [measurements, setMeasurements] = useState({});
  const [allMeasurements, setAllMeasurements] = useState([]);
  const [prs, setPrs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getBodyMeasurements(user.user_id).then(data => {
      setAllMeasurements(data);
      const todayM = data.find(d => d.date === today);
      if (todayM) setMeasurements(todayM.measurements || {});
    }).catch(() => {});
    api.getPRs(user.user_id).then(setPrs).catch(() => {});
  }, [user.user_id, today]);

  const saveMeasurements = async () => {
    setSaving(true);
    await api.saveBodyMeasurement({ user_id: user.user_id, date: today, measurements });
    setSaving(false);
    if (showToast) showToast('Measurements saved', 'success');
  };

  const fields = [
    { key: 'weight', label: 'Weight' },
    { key: 'bf', label: 'Body Fat %' },
    { key: 'chest', label: 'Chest' },
    { key: 'waist', label: 'Waist' },
    { key: 'armL', label: 'Arm L' },
    { key: 'armR', label: 'Arm R' },
    { key: 'thighL', label: 'Thigh L' },
    { key: 'thighR', label: 'Thigh R' },
    { key: 'hips', label: 'Hips' },
  ];

  const coreLifts = ['Bench Press', 'Back Squat', 'Deadlift', 'Overhead Press', 'Barbell Row'];

  // Group PRs by exercise
  const prsByExercise = {};
  prs.forEach(pr => {
    if (!prsByExercise[pr.exercise]) prsByExercise[pr.exercise] = [];
    prsByExercise[pr.exercise].push(pr);
  });

  // Weight chart data
  const weightData = allMeasurements.filter(m => m.measurements && m.measurements.weight).map(m => ({
    date: m.date,
    weight: m.measurements.weight,
  }));

  // Volume by day
  const workoutDays = Object.keys(workoutLogs).filter(d => workoutLogs[d].sets && workoutLogs[d].sets.length > 0).length;
  const totalVolume = Object.values(workoutLogs).reduce((sum, log) => {
    return sum + (log.sets || []).reduce((s, set) => s + (set.weight || 0) * (set.reps || 0), 0);
  }, 0);

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="progress-page">
      {/* Header */}
      <div className="glass rounded-xl p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-head text-lg sm:text-xl font-bold uppercase tracking-tight text-white">Progress</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-0.5">Measurements, PRs & trends</div>
          </div>
          {workoutDays > 0 && (
            <div className="text-right">
              <div className="font-mono text-sm font-bold text-brand">{workoutDays}</div>
              <div className="text-[9px] text-neutral-500 uppercase">Sessions</div>
            </div>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-0.5">
        {['measurements', 'prs', 'charts', 'report'].map(t => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className={`flex-shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-bold uppercase border transition-all active:scale-95 ${
              subTab === t ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-500 border-border'
            }`}
            data-testid={`progress-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Measurements */}
      {subTab === 'measurements' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-4">
            <div className="font-head text-lg font-bold uppercase text-white mb-4">Body Measurements</div>
            <div className="grid grid-cols-2 gap-3">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 block mb-1">{f.label}</label>
                  <input
                    type="number"
                    step="0.1"
                    value={measurements[f.key] || ''}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, [f.key]: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-black/50 border border-border text-white h-12 text-base rounded-md px-3 font-mono outline-none focus:border-brand"
                    data-testid={`measurement-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={saveMeasurements}
              disabled={saving}
              className="w-full mt-4 bg-brand text-white font-head font-bold uppercase min-h-[48px] rounded-md active:scale-95 transition-all"
              data-testid="save-measurements-btn"
            >
              {saving ? 'SAVING...' : 'SAVE MEASUREMENTS'}
            </button>
          </div>
        </div>
      )}

      {/* PRs */}
      {subTab === 'prs' && (
        <div className="space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white">Personal Records</div>
          {coreLifts.map(lift => {
            const history = prsByExercise[lift] || [];
            const best = history.length > 0 ? history[history.length - 1] : null;
            return (
              <div key={lift} className="glass rounded-xl p-4" data-testid={`pr-card-${lift}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-head text-base font-bold uppercase text-white">{lift}</div>
                    {best ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-xl font-bold text-brand">{best.weight}kg x {best.reps}</span>
                        <span className="text-xs text-neutral-500">est. 1RM: {best.estimated_1rm?.toFixed(1)}kg</span>
                      </div>
                    ) : (
                      <span className="text-sm text-neutral-500 mt-1">No PR yet</span>
                    )}
                  </div>
                  <Medal size={28} weight="bold" className={best ? 'text-warning' : 'text-neutral-700'} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Charts */}
      {subTab === 'charts' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-4">
            <div className="font-head text-lg font-bold uppercase text-white mb-4">Weight Trend</div>
            {weightData.length > 1 ? (
              <div className="h-40 flex items-end gap-1">
                {weightData.slice(-20).map((d, i) => {
                  const min = Math.min(...weightData.map(w => w.weight)) - 2;
                  const max = Math.max(...weightData.map(w => w.weight)) + 2;
                  const height = ((d.weight - min) / (max - min)) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-brand rounded-t-sm min-h-[2px]"
                        style={{ height: `${height}%` }}
                        title={`${d.date}: ${d.weight}kg`}
                      />
                      <span className="text-[8px] text-neutral-500 font-mono">{d.weight}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <TrendUp size={40} className="mx-auto mb-2" />
                <p className="text-sm">Log measurements to see trends</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report */}
      {subTab === 'report' && (
        <div className="space-y-3">
          <div className="font-head text-lg font-bold uppercase text-white">Summary Report</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Workouts', value: workoutDays, color: '#FF3B30' },
              { label: 'Total Volume', value: `${(totalVolume / 1000).toFixed(1)}t`, color: '#007AFF' },
              { label: 'Streak', value: `${user.streak_current || 0}d`, color: '#FFD60A' },
              { label: 'PRs Set', value: prs.length, color: '#32D74B' },
            ].map(stat => (
              <div key={stat.label} className="glass rounded-xl p-4 text-center">
                <div className="font-mono text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
